import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    TrendingUp,
    Ticket,
    DollarSign,
    BarChart3,
    Calendar,
    ArrowDownRight,
    Zap
} from 'lucide-react';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from 'recharts';
import OnboardingChecklist from '../onboarding/OnboardingChecklist';

const formatMoney = (value = 0) => `$${Number(value || 0).toLocaleString('es-AR')}`;

const getOccupancy = (sold = 0, total = 0) => {
    if (!total) return 0;
    return Math.round((sold / total) * 100);
};

const getShortName = (name = 'Evento') => (name.length > 14 ? `${name.slice(0, 14)}...` : name);

const AdminDashboard = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const { data } = await axios.get(`/api/clients/stats/${slug}`);
                setStats(data);
            } catch (err) {
                console.error('Error fetching stats:', err);
                setError('No se pudieron cargar las estadísticas.');
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [slug]);

    const chartData = useMemo(() => {
        if (!stats?.events) return [];
        return [...stats.events].sort((a, b) => new Date(a.date) - new Date(b.date)).map(e => ({
            name: getShortName(e.title || 'Evento'),
            ingresos: e.income || 0,
            gastos: e.expenses || 0,
            ganancia: e.netProfit || 0,
            ocupacion: getOccupancy(e.sold, e.total)
        }));
    }, [stats]);

    const analytics = useMemo(() => {
        const events = stats?.events || [];
        const summary = stats?.summary || {};
        const totalIncome = summary.totalIncome || 0;
        const totalTicketsSold = summary.totalTicketsSold || 0;
        const totalTicketsCapacity = summary.totalTicketsCapacity || 0;
        const totalNetProfit = summary.totalNetProfit || 0;
        const sortedByIncome = [...events].sort((a, b) => (b.income || 0) - (a.income || 0));
        const sortedByOccupancy = [...events]
            .filter(event => event.total > 0)
            .sort((a, b) => getOccupancy(b.sold, b.total) - getOccupancy(a.sold, a.total));
        const now = new Date();

        return {
            averageTicket: totalTicketsSold ? totalIncome / totalTicketsSold : 0,
            globalOccupancy: getOccupancy(totalTicketsSold, totalTicketsCapacity),
            topIncomeEvent: sortedByIncome[0] || null,
            topOccupancyEvent: sortedByOccupancy[0] || null,
            profitableEvents: events.filter(event => (event.netProfit || 0) > 0).length,
            lossEvents: events.filter(event => (event.netProfit || 0) < 0).length,
            activeEvents: events.filter(event => new Date(event.date) >= now).length,
            noSalesEvents: events.filter(event => (event.sold || 0) === 0).length,
            averageNetProfit: events.length ? totalNetProfit / events.length : 0
        };
    }, [stats]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin mb-4"></div>
                <p className="text-white/50 font-medium animate-pulse">Generando reporte ejecutivo...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center">
                    <p className="text-red-400 font-bold mb-2">Ops! Algo salió mal</p>
                    <p className="text-red-400/60 text-sm">{error}</p>
                </div>
            </div>
        );
    }

    const { summary, events, tournamentsSummary, tournaments, organizationType } = stats;

    if (organizationType === 'sports') {
        return (
            <div className="container mx-auto px-4 py-8 max-w-7xl animate-fade-in-up">
                <OnboardingChecklist slug={slug} />

                {/* Header */}
                <div data-tour="dashboard-summary" className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                            <span className="text-[10px] font-black uppercase tracking-[3px] text-white/40">Sports Analytics</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">Dashboard <span className="text-gradient">Deportivo</span></h1>
                        <p className="text-white/40 font-medium mt-2">Resumen simplificado de tus torneos e inscripciones de equipos.</p>
                    </div>
                    <div className="flex gap-3">
                        <button className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-white/10 transition-all">Exportar PDF</button>
                        <button onClick={() => navigate(`/${slug}/admin/tournaments/create`)} className="px-6 py-3 bg-brand-gradient rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 hover:scale-105 transition-all">Nuevo Torneo</button>
                    </div>
                </div>

                {/* Main Stats Grid for Sports */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    <StatCard
                        label="Ingresos Totales"
                        value={`$${tournamentsSummary?.totalIncome?.toLocaleString() || 0}`}
                        icon={<DollarSign size={24} />}
                        color="orange"
                    />
                    <StatCard
                        label="Equipos Inscritos"
                        value={`${tournamentsSummary?.totalEnrolledTeams || 0}`}
                        icon={<Ticket size={24} />}
                        color="blue"
                    />
                    <StatCard
                        label="Torneos Activos"
                        value={`${tournamentsSummary?.activeTournaments || 0}`}
                        icon={<TrendingUp size={24} />}
                        color="green"
                    />
                    <StatCard
                        label="Total Torneos"
                        value={`${tournamentsSummary?.totalTournaments || 0}`}
                        icon={<Calendar size={24} />}
                        color="red"
                    />
                </div>

                {/* Detailed Tournaments Table */}
                <div className="bg-glass rounded-[40px] border-white/5 shadow-2xl overflow-hidden mb-8">
                    <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-white/[0.01]">
                        <h3 className="text-lg font-black text-white flex items-center gap-3">
                            <BarChart3 size={20} className="text-primary-orange" />
                            Tus Torneos
                        </h3>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/[0.02]">
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Nombre</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Deporte</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Equipos</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Ingresos (Premio)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {tournaments?.map((t) => (
                                    <tr key={t.id} className="group hover:bg-white/[0.02] transition-colors cursor-pointer" onClick={() => window.location.href = `/${slug}/admin/tournaments/edit/${t.id}`}>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-white/40 group-hover:bg-primary-orange/20 group-hover:text-primary-orange transition-all border border-white/5">
                                                    <Calendar size={20} />
                                                </div>
                                                <div>
                                                    <p className="text-base font-black text-white leading-tight mb-1">{t.name}</p>
                                                    <p className={`text-xs font-bold ${t.status === 'Inscripciones Abiertas' ? 'text-green-400' : 'text-blue-400'}`}>{t.status}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="text-white/70 font-bold">{t.sport}</span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="w-full max-w-[140px]">
                                                <div className="flex justify-between text-[10px] font-black text-white/40 mb-2">
                                                    <span>{Math.round((t.enrolledTeams / (t.maxTeams || 1)) * 100)}%</span>
                                                    <span>{t.enrolledTeams}/{t.maxTeams}</span>
                                                </div>
                                                <div className="h-2 bg-white/5 rounded-full overflow-hidden border border-white/5">
                                                    <div
                                                        className="h-full bg-brand-gradient shadow-[0_0_10px_rgba(255,140,0,0.3)]"
                                                        style={{ width: `${(t.enrolledTeams / (t.maxTeams || 1)) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="text-lg font-black text-green-400">
                                                ${t.income?.toLocaleString()}
                                            </span>
                                            <p className="text-xs text-white/40 mt-1">Cuota: ${t.registrationFee}</p>
                                        </td>
                                    </tr>
                                ))}
                                {(!tournaments || tournaments.length === 0) && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-8 text-white/50 italic">Todavía no tenés torneos creados.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl animate-fade-in-up">
            <OnboardingChecklist slug={slug} />

            <div data-tour="dashboard-summary" className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                        <span className="text-[10px] font-black uppercase tracking-[3px] text-white/40">{organizationType === 'creator' ? 'Creator Analytics' : 'Eventos Analytics'}</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">Estadísticas</h1>
                    <p className="text-white/40 font-medium mt-2">Información financiera y comercial para entender cómo rinde cada evento.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <StatCard
                    label="Facturación Bruta"
                    value={formatMoney(summary.totalIncome)}
                    subValue={`${summary.totalTicketsSold || 0} entradas`}
                    icon={<DollarSign size={24} />}
                    color="orange"
                    detail="Total cobrado estimado por ventas."
                />
                <StatCard
                    label="Ganancia Neta"
                    value={formatMoney(summary.totalNetProfit)}
                    subValue={`prom. ${formatMoney(analytics.averageNetProfit)}`}
                    icon={<TrendingUp size={24} />}
                    color="green"
                    detail={`${analytics.profitableEvents} eventos en positivo`}
                    highlight
                />
                <StatCard
                    label="Ocupación Global"
                    value={`${analytics.globalOccupancy}%`}
                    subValue={`de ${summary.totalTicketsCapacity}`}
                    icon={<Ticket size={24} />}
                    color="blue"
                    percentage={analytics.globalOccupancy}
                    detail={`${summary.totalTicketsSold || 0} lugares vendidos`}
                />
                <StatCard
                    label="Ticket Promedio"
                    value={formatMoney(analytics.averageTicket)}
                    subValue="por venta"
                    icon={<BarChart3 size={24} />}
                    color="orange"
                    detail={`${summary.eventCount || 0} eventos analizados`}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                <InsightCard
                    label="Evento que más factura"
                    title={analytics.topIncomeEvent?.title || 'Sin ventas todavía'}
                    value={analytics.topIncomeEvent ? formatMoney(analytics.topIncomeEvent.income) : formatMoney(0)}
                    detail={analytics.topIncomeEvent ? `${analytics.topIncomeEvent.sold || 0} entradas vendidas` : 'Cuando haya ventas, aparece acá.'}
                    icon={<DollarSign size={20} />}
                />
                <InsightCard
                    label="Mejor ocupación"
                    title={analytics.topOccupancyEvent?.title || 'Sin cupo vendido'}
                    value={analytics.topOccupancyEvent ? `${getOccupancy(analytics.topOccupancyEvent.sold, analytics.topOccupancyEvent.total)}%` : '0%'}
                    detail={analytics.topOccupancyEvent ? `${analytics.topOccupancyEvent.sold || 0}/${analytics.topOccupancyEvent.total || 0} lugares` : 'Sirve para detectar qué evento tracciona más.'}
                    icon={<Ticket size={20} />}
                />
                <InsightCard
                    label="A revisar"
                    title={`${analytics.noSalesEvents} eventos sin ventas`}
                    value={`${analytics.lossEvents} en pérdida`}
                    detail={`${analytics.activeEvents} eventos próximos o vigentes.`}
                    icon={<ArrowDownRight size={24} />}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                <div className="lg:col-span-2 bg-glass p-8 rounded-[40px] border-white/5 shadow-2xl">
                    <div className="mb-8">
                        <h3 className="text-xl font-black text-white flex items-center gap-3 mb-2">
                            <Zap size={20} className="text-primary-orange" />
                            Rentabilidad por evento
                        </h3>
                        <p className="text-sm text-white/35 font-medium">Compará ingresos, gastos y ganancia neta sin salir de estadísticas.</p>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#ffffff40', fontSize: 10, fontWeight: 'bold' }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#ffffff40', fontSize: 10, fontWeight: 'bold' }}
                                    tickFormatter={value => `$${Number(value).toLocaleString('es-AR')}`}
                                />
                                <Tooltip
                                    cursor={{ fill: '#ffffff05' }}
                                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #ffffff10', borderRadius: '16px', fontSize: '12px' }}
                                    formatter={value => formatMoney(value)}
                                />
                                <Bar dataKey="ingresos" fill="#22c55e" radius={[8, 8, 0, 0]} />
                                <Bar dataKey="gastos" fill="#ef4444" radius={[8, 8, 0, 0]} />
                                <Bar dataKey="ganancia" fill="#FF8C00" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-glass p-8 rounded-[40px] border-white/5 shadow-2xl">
                    <div className="mb-8">
                        <h3 className="text-xl font-black text-white flex items-center gap-3 mb-2">
                            <Ticket size={20} className="text-blue-400" />
                            Ocupación
                        </h3>
                        <p className="text-sm text-white/35 font-medium">Porcentaje vendido de la capacidad total.</p>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#ffffff40', fontSize: 10, fontWeight: 'bold' }}
                                />
                                <Tooltip
                                    cursor={{ fill: '#ffffff05' }}
                                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #ffffff10', borderRadius: '16px' }}
                                    formatter={value => `${value}%`}
                                />
                                <Bar dataKey="ocupacion" radius={[10, 10, 0, 0]}>
                                    {chartData.map((entry) => (
                                        <Cell key={`cell-${entry.name}`} fill={entry.ocupacion >= 80 ? '#22c55e' : entry.ocupacion >= 40 ? '#FF8C00' : '#3b82f6'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="bg-glass rounded-[40px] border-white/5 shadow-2xl overflow-hidden mb-8">
                <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-white/[0.01]">
                    <h3 className="text-lg font-black text-white flex items-center gap-3">
                        <BarChart3 size={20} className="text-primary-orange" />
                        Detalle de rentabilidad
                    </h3>
                    <p className="text-xs font-bold text-white/30">Lectura por evento para tomar decisiones.</p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-white/[0.02]">
                                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Evento</th>
                                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Ocupación</th>
                                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Ingresos</th>
                                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Gastos</th>
                                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Ganancia</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {events.map((event) => (
                                <tr key={event.id} className="group hover:bg-white/[0.02] transition-colors">
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-white/40 group-hover:bg-primary-orange/20 group-hover:text-primary-orange transition-all border border-white/5">
                                                <Calendar size={20} />
                                            </div>
                                            <div>
                                                <p className="text-base font-black text-white leading-tight mb-1">{event.title}</p>
                                                <p className="text-xs font-bold text-white/30">{new Date(event.date).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="w-full max-w-[140px]">
                                            <div className="flex justify-between text-[10px] font-black text-white/40 mb-2">
                                                <span>{getOccupancy(event.sold, event.total)}%</span>
                                                <span>{event.sold}/{event.total}</span>
                                            </div>
                                            <div className="h-2 bg-white/5 rounded-full overflow-hidden border border-white/5">
                                                <div
                                                    className="h-full bg-brand-gradient shadow-[0_0_10px_rgba(255,140,0,0.3)]"
                                                    style={{ width: `${getOccupancy(event.sold, event.total)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <span className="text-sm font-black text-white">{formatMoney(event.income)}</span>
                                    </td>
                                    <td className="px-8 py-6">
                                        <span className="text-sm font-black text-red-300/90">{formatMoney(event.expenses)}</span>
                                    </td>
                                    <td className="px-8 py-6">
                                        <span className={`text-lg font-black ${event.netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {formatMoney(event.netProfit)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {events.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="text-center py-10 text-white/40 font-bold">Todavía no hay eventos para analizar.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, subValue, icon, color, detail, percentage, highlight }) => {
    const colors = {
        orange: 'bg-primary-orange/10 text-primary-orange border-primary-orange/20',
        blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        red: 'bg-red-500/10 text-red-400 border-red-500/20',
        green: 'bg-green-500/10 text-green-400 border-green-500/20',
    };

    return (
        <div className={`bg-glass p-8 rounded-[32px] border-white/5 shadow-xl relative overflow-hidden transition-all hover:scale-[1.02] hover:border-white/10 group ${highlight ? 'ring-1 ring-primary-orange/30' : ''}`}>
            {highlight && <div className="absolute top-0 right-0 bg-brand-gradient text-[8px] font-black text-white px-4 py-1.5 rounded-bl-2xl uppercase tracking-[2px]">Neto</div>}

            <div className="flex items-start justify-between mb-6">
                <div className={`p-4 rounded-2xl border ${colors[color]} group-hover:scale-110 transition-transform duration-500`}>
                    {icon}
                </div>
                {percentage !== undefined && (
                    <div className="flex flex-col items-end">
                        <span className="text-[10px] font-black text-white/20 uppercase tracking-widest mb-1">Capacidad</span>
                        <span className="text-xs font-black text-blue-400 bg-blue-400/10 px-2 py-1 rounded-lg border border-blue-400/20">
                            {percentage}%
                        </span>
                    </div>
                )}
            </div>

            <div>
                <h4 className="text-[10px] font-black text-white/20 uppercase tracking-[3px] mb-2">{label}</h4>
                <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-black text-white tracking-tight">{value}</p>
                    {subValue && <span className="text-xs font-bold text-white/20">{subValue}</span>}
                </div>
                {detail && <p className="text-xs font-bold text-white/30 mt-3 leading-relaxed">{detail}</p>}
            </div>
        </div>
    );
};

const InsightCard = ({ label, title, value, detail, icon }) => (
    <div className="bg-glass p-6 rounded-[28px] border-white/5 shadow-xl">
        <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/5 flex items-center justify-center text-primary-orange shrink-0">
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[3px] text-white/25 mb-2">{label}</p>
                <h4 className="text-lg font-black text-white truncate">{title}</h4>
                <p className="text-2xl font-black text-primary-orange mt-2">{value}</p>
                <p className="text-xs font-bold text-white/30 mt-2 leading-relaxed">{detail}</p>
            </div>
        </div>
    </div>
);

export default AdminDashboard;
